import React, { useContext, useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  TextField,
  Pagination,
  InputAdornment,
} from '@mui/material';
import { Search, Inventory2Outlined } from '@mui/icons-material';

import Divider from '@mui/material/Divider';
// CONTEXT
import useAllocationContext from '../context/useAllocationContext';
import { numberFormat } from '../../../helpers/helperFunctions';
// const inventory = [
//   {
//     product_code: 'AX',
//     product_name: 'Amoxicillin 500mg',
//     label: 'AMX-500',
//     unallocation: 108,
//     allocation: 0,
//     total_qty: 240,
//   },
//   {
//     product_code: 'AT',
//     product_name: 'Atorvastatin 20mg',
//     label: 'ATV-20',
//     unallocation: 108,
//     allocation: 0,
//     total_qty: 240,
//   },
//   {
//     product_code: 'MT',
//     product_name: 'Metformin 850mg',
//     label: 'MTF-850',
//     unallocation: 108,
//     allocation: 0,
//     total_qty: 240,
//   },
// ];

export default function InventoryCards() {
  const { inventory } = useContext(useAllocationContext);

  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');

  const rowsPerPage = 5;
  const isSearching = searchText.trim() !== '';

  const uniqueItems = useMemo(() => {
    const items = inventory.items ?? [];

    // Collapse repeated entries sharing the same product_code to avoid
    // duplicate cards (and duplicate React keys) across pages.
    return Array.from(new Map(items.map((item) => [item.product_code, item])).values());
  }, [inventory.items]);

  const filteredItems = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) return uniqueItems;

    return uniqueItems.filter(
      (item) =>
        item.product_name?.toLowerCase().includes(search) ||
        item.product_code?.toLowerCase().includes(search),
    );
  }, [uniqueItems, searchText]);

  const pageCount = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1,
          py: 1,
        }}
      >
        {/* Left */}
        <Box display='flex' alignItems='center' gap={1}>
          <Inventory2Outlined color='success' fontSize='small' />

          <Typography fontWeight={700}>Confirmed inventory</Typography>

          <Typography variant='body2' color='text.secondary'>
            Remaining decreases as you allocate to MDs
          </Typography>
        </Box>

        {/* Right */}
        <Box display='flex' alignItems='center' gap={3}>
          <Typography variant='body2' color='text.secondary'>
            Total confirmed:{' '}
            <Typography component='span' fontWeight={700} color='text.primary'>
              {numberFormat(inventory?.total_confirmed)}
            </Typography>
          </Typography>

          <Typography variant='body2' color='text.secondary'>
            Allocated:{' '}
            <Typography component='span' fontWeight={700} color='success.main'>
              {numberFormat(inventory?.total_allocated)}
            </Typography>
          </Typography>

          <Typography variant='body2' color='text.secondary'>
            Remaining:{' '}
            <Typography component='span' fontWeight={700} color='text.primary'>
              {numberFormat(inventory?.total_remaining)}
            </Typography>
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mx: -3, my: 2 }} />
      <Box>
        <TextField
          size='small'
          value={searchText}
          onChange={handleSearch}
          placeholder='Search by product name/code '
          sx={{ width: 380 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box mt={3} pb={3}>
        <Box sx={{ width: '100%' }}>
          {inventory.loading ? (
            <LinearProgress sx={{ width: '100%' }} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                pb: 1,
              }}
            >
              {paginatedItems.map((item) => (
                <Paper
                  key={item.product_code}
                  variant='outlined'
                  sx={{
                    flex: {
                      xs: '0 0 100%',
                      sm: '0 0 calc(50% - 8px)',
                      md: '0 0 calc(20% - 12.8px)',
                    },
                    maxWidth: { md: 'calc(20% - 12.8px)' },
                    p: isSearching ? 1 : 2,
                    borderRadius: 2,
                    boxSizing: 'border-box',
                  }}
                >
                  <Box display='flex' gap={2}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 1,
                        bgcolor: '#C8E6C9',
                        color: '#2E7D32',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {item.product_name
                        ?.split(' ')
                        .filter((word) => /^[A-Za-z]/.test(word))
                        .map((word) => word[0])
                        .join('')
                        .toUpperCase()}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontSize: 15,
                          lineHeight: 1.4,
                          minHeight: 56,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {item.product_name}
                        <Typography variant='body2' color='text.secondary' noWrap>
                          {item.product_code}
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={
                      item.total_qty > 0
                        ? Math.min(100, Math.max(0, (item.allocated_qty / item.total_qty) * 100))
                        : 0
                    }
                    sx={{
                      my: 2,
                      height: 7,
                      borderRadius: 2,
                      backgroundColor: '#f3f3f3 !important',

                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#2E7D32 !important',
                      },
                    }}
                  />
                  <Box display='flex' justifyContent='space-between'>
                    <Typography fontWeight={700} fontSize={12}>
                      {item.allocated_qty} / {item.total_qty}
                    </Typography>

                    <Typography variant='body2' color='text.secondary'>
                      {item.balance_qty} left
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Divider sx={{ mx: -3, my: 2 }} />
      <Box display='flex' justifyContent='flex-end' mt={1} pl={1}>
        <Pagination page={page} count={pageCount} onChange={(e, value) => setPage(value)} />
      </Box>
    </Paper>
  );
}
